import React, { useState, useMemo } from "react";
import {
  Code2,
  Star,
  Maximize,
  FileText,
  Lock,
  Download,
  Globe,
  AlertTriangle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { apiService } from "../../services";
import CourseCard from "../course/CourseCard";
import { hasCourseAccess } from "../course/access";

type Language = "en" | "fr" | "de" | "es" | "ja" | "ru" | "zh";

interface UserDownload {
  id: string;
  type:
    | "bookpdf"
    | "bookepub"
    | "samplecode"
    | "coursedeck"
    | "bonus"
    | "template";
  title: string;
  description: string;
  changelog: string;
  version: string;
  fileSize: number;
  fileName: string;
  updatedAt: string;
  language?: Language;
  isTranslation?: boolean;
}

interface UserDownloadsProps {
  downloads: UserDownload[];
  hasActiveSubscription: boolean;
  customerTier: string;
  onPurchaseClick: (tier: string) => void;
}

// Language display names
const LANGUAGE_NAMES: Record<Language, { name: string; native: string }> = {
  en: { name: "English", native: "English" },
  fr: { name: "French", native: "Français" },
  de: { name: "German", native: "Deutsch" },
  es: { name: "Spanish", native: "Español" },
  ja: { name: "Japanese", native: "日本語" },
  ru: { name: "Russian", native: "Русский" },
  zh: { name: "Chinese", native: "中文" },
};

const UserDownloads: React.FC<UserDownloadsProps> = ({
  downloads,
  hasActiveSubscription,
  customerTier,
  onPurchaseClick,
}) => {
  const { t } = useTranslation("dashboard");
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [selectedTranslations, setSelectedTranslations] = useState<
    Record<string, Language>
  >({});

  const toggleCardExpansion = (cardId: string) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(cardId)) {
      newExpanded.delete(cardId);
    } else {
      newExpanded.add(cardId);
    }
    setExpandedCards(newExpanded);
  };

  const handleDownload = async (downloadId: string, fileName: string) => {
    try {
      const downloadUrl = await apiService.getDownloadUrl(downloadId);

      // Create download link and trigger download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      alert(t("contentLibrary.downloadFailed"));
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  // Group downloads: English versions with their translations.
  // Exclude samplecode (shown in PremiumSamples) and coursedeck (shown on
  // /course) — otherwise the 16 chapter decks bury the book in this list.
  const bookDownloads = useMemo(
    () =>
      downloads.filter(
        (d) => d.type !== "samplecode" && d.type !== "coursedeck"
      ),
    [downloads]
  );

  const groupedDownloads = useMemo(() => {
    // Separate English and translations
    const english = bookDownloads.filter(
      (d) => !d.language || d.language === "en"
    );
    const translations = bookDownloads.filter(
      (d) => d.language && d.language !== "en"
    );

    // Group translations by type
    const translationsByType: Record<string, UserDownload[]> = {};
    for (const t of translations) {
      if (!translationsByType[t.type]) {
        translationsByType[t.type] = [];
      }
      translationsByType[t.type].push(t);
    }

    return { english, translationsByType };
  }, [bookDownloads]);

  const getDownloadTypeInfo = (type: string) => {
    switch (type) {
      case "bookpdf":
        return {
          icon: (
            <div className="w-12 h-16 sm:w-16 sm:h-20 rounded overflow-hidden relative">
              <img
                src="/images/bookcover.webp"
                alt="Multi-Agent Systems Book Cover"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 right-0 bg-red-600 text-white text-xs px-1 py-0.5 rounded-tl-md font-medium">
                PDF
              </div>
            </div>
          ),
          label: t("contentLibrary.types.bookPdf"),
          description: t("contentLibrary.types.bookPdfDesc"),
          color: "bg-red-50 text-red-700 border-red-200",
        };
      case "bookepub":
        return {
          icon: (
            <div className="w-12 h-16 sm:w-16 sm:h-20 rounded overflow-hidden relative">
              <img
                src="/images/bookcover.webp"
                alt="Multi-Agent Systems Book Cover"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 right-0 bg-blue-600 text-white text-xs px-1 py-0.5 rounded-tl-md font-medium">
                EPUB
              </div>
            </div>
          ),
          label: t("contentLibrary.types.bookEpub"),
          description: t("contentLibrary.types.bookEpubDesc"),
          color: "bg-blue-50 text-blue-700 border-blue-200",
        };
      case "samplecode":
        return {
          icon: (
            <div className="w-12 h-16 sm:w-16 sm:h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center text-white">
              <Code2 className="w-6 h-6 sm:w-8 sm:h-8" />
            </div>
          ),
          label: t("contentLibrary.types.sampleCode"),
          description: t("contentLibrary.types.sampleCodeDesc"),
          color: "bg-green-50 text-green-700 border-green-200",
        };
      case "bonus":
        return {
          icon: (
            <div className="w-12 h-16 sm:w-16 sm:h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center text-white">
              <Star className="w-6 h-6 sm:w-8 sm:h-8" />
            </div>
          ),
          label: t("contentLibrary.types.bonus"),
          description: t("contentLibrary.types.bonusDesc"),
          color: "bg-purple-50 text-purple-700 border-purple-200",
        };
      case "template":
        return {
          icon: (
            <div className="w-12 h-16 sm:w-16 sm:h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center text-white">
              <Maximize className="w-6 h-6 sm:w-8 sm:h-8" />
            </div>
          ),
          label: t("contentLibrary.types.template"),
          description: t("contentLibrary.types.templateDesc"),
          color: "bg-orange-50 text-orange-700 border-orange-200",
        };
      default:
        return {
          icon: (
            <div className="w-12 h-16 sm:w-16 sm:h-20 bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg flex items-center justify-center text-white">
              <FileText className="w-6 h-6 sm:w-8 sm:h-8" />
            </div>
          ),
          label: t("contentLibrary.types.file"),
          description: t("contentLibrary.types.fileDesc"),
          color: "bg-tertiary text-primary border-tertiary",
        };
    }
  };

  // No subscription state
  if (!hasActiveSubscription) {
    return (
      <div className="bg-primary rounded-xl border border-secondary p-6 sm:p-8">
        <div className="text-center py-8 sm:py-12">
          <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-accent/20 to-accent/30 rounded-2xl flex items-center justify-center">
            <Lock className="w-8 h-8 sm:w-10 sm:h-10 text-accent" />
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-primary mb-2 sm:mb-3">
            {t("contentLibrary.unlockTitle")}
          </h3>
          <p className="text-secondary mb-6 sm:mb-8 max-w-md mx-auto text-sm sm:text-base">
            {t("contentLibrary.unlockDescription")}
          </p>
          <button
            onClick={() => onPurchaseClick("essential")}
            className="bg-accent text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg hover:bg-accent/90 transition-colors font-semibold text-base sm:text-lg"
          >
            {t("contentLibrary.viewPricing")}
          </button>
        </div>
      </div>
    );
  }

  // No downloads available at all
  if (groupedDownloads.english.length === 0) {
    return (
      <div className="bg-primary rounded-xl border border-secondary p-6 sm:p-8">
        <div className="text-center py-8 sm:py-12">
          <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-accent/20 to-accent/30 rounded-2xl flex items-center justify-center">
            <Download className="w-8 h-8 sm:w-10 sm:h-10 text-accent" />
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-primary mb-2 sm:mb-3">
            {t("contentLibrary.comingSoonTitle")}
          </h3>
          <p className="text-secondary max-w-md mx-auto text-sm sm:text-base">
            {t("contentLibrary.comingSoonDescription", { tier: customerTier })}
          </p>
        </div>
      </div>
    );
  }

  // Render a download card with optional translations inline
  const renderDownloadCard = (
    download: UserDownload,
    translations?: UserDownload[]
  ) => {
    const typeInfo = getDownloadTypeInfo(download.type);
    const isExpanded = expandedCards.has(download.id);
    const cardKey = `${download.type}-translations`;
    const selectedLang = selectedTranslations[cardKey];
    const selectedTranslation = translations?.find(
      (t) => t.language === selectedLang
    );

    return (
      <div
        key={download.id}
        className="group border border-tertiary rounded-lg p-3 sm:p-4 hover:border-accent/30 transition-all duration-200 bg-secondary/20 hover:bg-secondary/40"
      >
        <div className="flex items-start space-x-3 sm:space-x-4">
          {/* Download Icon/Cover */}
          <div className="flex-shrink-0">{typeInfo.icon}</div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="mb-2">
              <h3 className="text-lg sm:text-xl font-semibold text-primary mb-1 group-hover:text-accent transition-colors leading-tight">
                {download.title}
              </h3>
              <p className="text-secondary text-sm mb-2 line-clamp-2">
                {download.description}
              </p>
            </div>

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-2 text-xs mb-3">
              <span className="text-tertiary">
                {t("contentLibrary.version", { version: download.version })}
              </span>
              <span className="text-tertiary">
                {formatFileSize(download.fileSize)}
              </span>
            </div>

            {/* Changelog */}
            {download.changelog && (
              <div className="mb-3">
                <div className="text-xs font-medium text-secondary mb-1">
                  {t("contentLibrary.whatsNew", {
                    version: `v${download.version}`,
                  })}
                </div>
                <p
                  className={`text-xs sm:text-sm text-primary ${
                    isExpanded ? "" : "line-clamp-3"
                  }`}
                >
                  {download.changelog}
                </p>
                <button
                  onClick={() => toggleCardExpansion(download.id)}
                  className="text-xs text-accent hover:text-accent/80 font-medium mt-1"
                >
                  {isExpanded ? "Show less" : "Show more"}
                </button>
              </div>
            )}

            {/* Update Date */}
            <div className="text-tertiary text-xs mb-3">
              {t("contentLibrary.updated", {
                date: new Date(download.updatedAt).toLocaleDateString(),
              })}
            </div>

            {/* Download Button - Full Width */}
            <button
              onClick={() => handleDownload(download.id, download.fileName)}
              className="w-full bg-accent text-white px-4 py-2.5 rounded-lg hover:bg-accent/90 transition-colors font-medium flex items-center justify-center gap-2 text-sm"
            >
              <Download className="w-4 h-4" />
              {t("contentLibrary.download")}
            </button>

            {/* Translations Section - Only for book types with translations */}
            {translations && translations.length > 0 && (
              <div className="mt-4 pt-3 border-t border-tertiary/50">
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="w-4 h-4 text-secondary" />
                  <span className="text-xs font-medium text-secondary">
                    Also available in:
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={selectedLang || ""}
                    onChange={(e) =>
                      setSelectedTranslations((prev) => ({
                        ...prev,
                        [cardKey]: e.target.value as Language,
                      }))
                    }
                    className="flex-1 min-w-0 text-sm border border-tertiary rounded-md px-2 py-1.5 bg-primary text-primary focus:outline-none focus:ring-1 focus:ring-accent"
                  >
                    <option value="">Select language...</option>
                    {translations.map((t) => (
                      <option key={t.id} value={t.language}>
                        {LANGUAGE_NAMES[t.language!]?.native ||
                          t.language}{" "}
                        ({LANGUAGE_NAMES[t.language!]?.name || t.language})
                      </option>
                    ))}
                  </select>

                  {selectedTranslation && (
                    <button
                      onClick={() =>
                        handleDownload(
                          selectedTranslation.id,
                          selectedTranslation.fileName
                        )
                      }
                      className="px-3 py-1.5 bg-secondary/50 text-primary rounded-md hover:bg-secondary transition-colors text-sm font-medium flex items-center gap-1"
                    >
                      <Download className="w-3 h-3" />
                      Download
                    </button>
                  )}
                </div>

                {/* Translation disclaimer */}
                <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-600">
                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>Auto-translated. May contain errors.</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Downloads available
  return (
    <div className="bg-primary rounded-lg border border-secondary overflow-hidden">
      {/* Header */}
      <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-tertiary bg-secondary/50">
        <h2 className="text-xl sm:text-2xl font-bold text-primary mb-1">
          {t("contentLibrary.title")}
        </h2>
        <p className="text-secondary text-sm sm:text-base">
          {t("contentLibrary.itemsAvailable", {
            count:
              groupedDownloads.english.length +
              (hasCourseAccess(customerTier) ? 1 : 0),
          })}
        </p>
      </div>

      {/* Downloads Grid */}
      <div className="p-3 sm:p-4">
        <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {groupedDownloads.english.map((download) =>
            renderDownloadCard(
              download,
              groupedDownloads.translationsByType[download.type]
            )
          )}
          {/* Not a download: opens the catalog. Renders nothing unentitled. */}
          <CourseCard customerTier={customerTier} />
        </div>
      </div>
    </div>
  );
};

export default UserDownloads;
